import React, { useState, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, FormGroup, Label, Input } from 'reactstrap';
import PagosPlanillaService from '../../axios_services/pagosplanilla.service';
import { AppContext } from '../../application/provider';

const ModalValidacionPlanilla = ({ isOpen, onClose, onRefresh, periodo, areaId, data, situacion }) => {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);

    // Filtrar solo los registrados
    const registros = data.filter(d => d.registrado);
    
    // Obtener el estado de validación actual
    const currentValId = registros.length > 0 ? registros[0].validacion_id : null;

    const areaNames = {
        '1': 'ADMINISTRACIÓN',
        '2': 'GERENCIA DE NEGOCIOS',
        '3': 'TALENTO HUMANO',
        '4': 'GERENCIA',
    };

    const getEstadoNombre = (valId) => {
        if (valId === 0) return 'PENDIENTE';
        if (valId === 2) return 'VALIDADO POR SUBGERENCIA ADMINISTRATIVA';
        if (valId === 3) return 'VALIDADO POR GERENCIA DE NEGOCIOS';
        if (valId === 4) return 'VALIDADO POR SUBGERENCIA ADMINISTRATIVA';
        if (valId === 5) return 'VALIDADO POR GERENCIA GENERAL';
        if (valId === 6) return 'OBSERVADO / RECHAZADO';
        return 'DESCONOCIDO';
    };

    const handleAction = async (accion) => {
        setIsLoading(true);
        try {
            const res = await PagosPlanillaService.validarPlanilla({
                periodo: periodo,
                area_id: parseInt(areaId),
                accion: accion,
                usuario_id: state.user?.usuario_id,
                situacion_id: situacion ? parseInt(situacion) : null
            });
            
            const { data: responseData } = res;

            if (responseData.success) {
                alert(responseData.message || 'Operación realizada con éxito');
                if (onRefresh) onRefresh();
                onClose();
            } else {
                alert('Error: ' + (responseData.message || 'No se pudo procesar la solicitud'));
            }
        } catch (error) {
            console.error('Error al validar planilla:', error);
            const msg = error.response?.data?.message || error.message || 'Error de conexión';
            alert('Ocurrió un error: ' + msg);
        } finally {
            setIsLoading(false);
        }
    };

    // Botones de acción
    let canApprove = false;
    let approveMessage = '';
    const userProfile = state.user?.perfil_id ? parseInt(state.user.perfil_id) : 0;
    const currentAreaStr = String(areaId);

    // --- CONFIGURACIÓN ACTUAL: ADMINISTRADO POR PERFIL 26, PERFIL 1 Y PERFIL 9 ---
    if (userProfile === 8) {
        canApprove = true;
    } else if (userProfile === 26 && (currentValId === 0 || currentValId === 6)) {
        // Paso 1: Subgerencia Administrativa realiza la 1ra validación para cualquier área
        canApprove = true;
    } else if (userProfile === 1 && currentAreaStr === '2' && currentValId === 2) {
        // Paso 2 (Comercial / Negocios): Gerente de Negocios realiza la 2da validación tras la Subgerencia
        canApprove = true;
    } else if (userProfile === 9 && (
        (['1', '4'].includes(currentAreaStr) && (currentValId === 2 || currentValId === 4)) ||
        (currentAreaStr === '2' && currentValId === 3)
    )) {
        // Paso Final: Gerencia General aprueba (tras Subgerencia en Adm/Gerencia o tras Gerencia de Negocios en Comercial)
        canApprove = true;
    } else {
        if (currentValId >= 2 && userProfile === 26 && (currentValId !== 0 && currentValId !== 6)) approveMessage = 'Ya fue validado por Subgerencia Administrativa.';
        else if (currentValId >= 3 && userProfile === 1) approveMessage = 'Ya fue validado por Gerencia de Negocios.';
        else if (currentValId === 5 && userProfile === 9) approveMessage = 'Ya fue validado por Gerencia General (Finalizado).';
        else approveMessage = 'No es su turno para validar o ya fue validado.';
    }

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="md" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>VALIDACIÓN MASIVA DE PLANILLA</h1>
                    <button className="close-btn" onClick={onClose} disabled={isLoading}>X</button>
                </div>
            </ModalHeader>
            
            <div className="modal-body-footer">
                <ModalBody className="modal-body p-4">
                    {registros.length === 0 ? (
                        <Alert color="warning" className="m-0">
                            No hay registros consolidados en esta área para el periodo seleccionado.
                        </Alert>
                    ) : (
                        <div className="px-4">
                            <p className="text-muted mb-4" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                                Resumen de validación masiva para el periodo seleccionado.
                            </p>

                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Periodo:</Label>
                                <Input 
                                    type="text"
                                    className="form-control"
                                    value={periodo}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                />
                            </FormGroup>
                            
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Área:</Label>
                                <Input 
                                    type="text"
                                    className="form-control"
                                    value={areaNames[areaId] || `Área ${areaId}`}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                />
                            </FormGroup>

                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Estado actual:</Label>
                                <div className="d-flex align-items-center">
                                    <Input 
                                        type="text"
                                        className="form-control"
                                        value={getEstadoNombre(currentValId)}
                                        readOnly
                                        style={{ 
                                            backgroundColor: '#f8f9fa',
                                            color: '#333',
                                            fontWeight: '400'
                                        }}
                                    />
                                </div>
                            </FormGroup>

                            <FormGroup className="mb-4">
                                <Label className="fw-bold">Total registros:</Label>
                                <Input 
                                    type="text"
                                    className="form-control"
                                    value={registros.length}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                />
                            </FormGroup>

                            {!canApprove && approveMessage && (
                                <Alert color="secondary" className="text-center font-bold m-0 p-2 text-sm">
                                    {approveMessage}
                                </Alert>
                            )}
                        </div>
                    )}
                </ModalBody>
                
                <ModalFooter className="flex justify-end p-4 border-t border-gray-200" style={{ padding: '15px' }}>
                    {registros.length > 0 && (
                        <div className="flex gap-4">
                            <Button 
                                color="danger" 
                                onClick={() => handleAction('rechazar')} 
                                disabled={isLoading}
                                style={{ width: '130px', borderRadius: '8px' }}
                            >
                                Rechazar
                            </Button>
                            {canApprove && (
                                <Button 
                                    color="success" 
                                    onClick={() => handleAction('validar')} 
                                    disabled={isLoading}
                                    style={{ width: '130px', borderRadius: '8px' }}
                                >
                                    Aprobar
                                </Button>
                            )}
                        </div>
                    )}
                </ModalFooter>
            </div>
        </Modal>
    );
};

export default ModalValidacionPlanilla;
